/* eslint-disable react/jsx-no-undef */
/* eslint-disable max-lines-per-function */
import { faEnvelope, faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  GridLegacy as Grid,
  Link,
  InputAdornment,
  LinearProgress,
  Typography,
} from '@mui/material';
import { useFormHook } from '@monorepo/react/hook-form-helper';
import { CustomTextField } from '@monorepo/react/material-form-inputs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SubmitFunctionResult } from '../types';
import axios from 'axios';
import { joinPath } from './path.utils';
import { Link as RouterLink } from 'react-router-dom';

export interface LocalLoginFormProps {
  submitFunction: (
    username: string,
    password: string,
  ) => Promise<SubmitFunctionResult>;
  updateTokenFunction: (token: string, refreshToken: string) => void;
  pathPrefix?: string;
  inlineErrorMessage?: string | null;
}

const defaultValues = {
  id: '',
  password: '',
};

export const LocalLoginForm: React.FC<LocalLoginFormProps> = ({
  submitFunction,
  updateTokenFunction,
  pathPrefix = '',
  inlineErrorMessage,
}) => {
  const { t } = useTranslation();

  const onSubmit = React.useCallback(
    async (formValues: { id: string; password: string }) => {
      const result = await submitFunction(formValues.id, formValues.password);

      // keep returning errors to hook-form-helper (if it uses this)
      if ((result as any)?.errors) return { errors: (result as any).errors };

      if ((result as any)?.data) {
        updateTokenFunction(
          (result as any).data.login.accessToken as string,
          (result as any).data.login.refreshToken as string,
        );
      }

      return result;
    },
    [submitFunction, updateTokenFunction],
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useFormHook({
    defaultValues,
    preventReset: true,
    onSubmit,
  });

  const [currentDomain] = React.useState(() => window.location.hostname);
  const [privacyLink, setPrivacyLink] = React.useState('');

  React.useEffect(() => {
    const fetchData = async () => {
      if (currentDomain === 'localhost') return;
      const response = await axios.get('/api/appVersion');
      if (!response) return;
      setPrivacyLink(response.data.privacyLink);
    };

    fetchData();
    const intervalId = setInterval(fetchData, 10 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [currentDomain]);

  // This now comes from login-card.tsx (built from result.errors)
  const resolvedInlineErrorMessage = inlineErrorMessage;

  return (
    <form noValidate autoComplete="off" onSubmit={handleSubmit}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <CustomTextField
            errors={errors}
            {...register('id', { required: true })}
            label={String(t('login.email'))}
            name="id"
            autoComplete="current-username"
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiInputLabel-root': {
                color: '#6f747b',
                fontSize: '0.95rem',
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#6f747b' },
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#eaf2ff',
                '& fieldset': { borderColor: '#b7bdc6' },
                '&:hover fieldset': { borderColor: '#98a0aa' },
                '&.Mui-focused fieldset': { borderColor: '#8ea7c6' },
              },
              '& input': {
                padding: '14px 14px',
                fontSize: '0.8rem',
                color: '#1f2a33',
              },
              '& input:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0 1000px #eaf2ff inset',
                WebkitTextFillColor: '#1f2a33',
                caretColor: '#1f2a33',
                fontSize: '0.8rem',
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{
                    color: '#6b7077',
                    paddingLeft: '12px',
                    marginRight: '4px',
                    borderLeft: '1px solid #c7ccd2',
                  }}
                >
                  <FontAwesomeIcon size="lg" icon={faEnvelope} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {resolvedInlineErrorMessage ? (
          <Grid item xs={12} sx={{ mt: -4, textAlign: 'left' }}>
            <Typography variant="body2" sx={{ color: 'error.main' }}>
              {resolvedInlineErrorMessage}
            </Typography>
          </Grid>
        ) : null}

        <Grid item xs={12}>
          <CustomTextField
            errors={errors}
            type="password"
            {...register('password', { required: true })}
            label={String(t('login.password'))}
            name="password"
            autoComplete="current-password"
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiInputLabel-root': {
                color: '#6f747b',
                fontSize: '0.95rem',
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#6f747b' },
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#eaf2ff',
                '& fieldset': { borderColor: '#b7bdc6' },
                '&:hover fieldset': { borderColor: '#98a0aa' },
                '&.Mui-focused fieldset': { borderColor: '#8ea7c6' },
              },
              '& input': {
                padding: '14px 14px',
                fontSize: '0.8rem',
                color: '#1f2a33',
              },
              '& input:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0 1000px #eaf2ff inset',
                WebkitTextFillColor: '#1f2a33',
                caretColor: '#1f2a33',
                fontSize: '0.8rem',
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{
                    color: '#6b7077',
                    paddingLeft: '12px',
                    marginRight: '4px',
                    borderLeft: '1px solid #c7ccd2',
                  }}
                >
                  <FontAwesomeIcon size="lg" icon={faLock} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
            >
              {String(t('login.submitButton'))}
            </Button>

            <Grid item>
              <Grid
                container
                direction="column"
                justifyContent="flex-end"
                alignItems="flex-end"
                sx={{ gap: 1 }}
              >
                <Link
                  component={RouterLink}
                  to={joinPath(pathPrefix, 'requestPasswordReset')}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {String(t('resetPassword.link'))}
                </Link>
                <Link
                  href={privacyLink}
                  target="_blank"
                  align="right"
                  sx={{ fontWeight: 700, textDecoration: 'none' }}
                >
                  {String(t('dataPrivacy'))}
                </Link>
              </Grid>
            </Grid>

            {isSubmitting && (
              <Grid item xs={12} style={{ padding: '10px', margin: '20px 0' }}>
                <LinearProgress />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};
